/**
 * Subscribe & save — purchase-option radios, delivery frequency, and
 * variant-aware plan prices. Writes `selling_plan` for add-to-cart.
 */
(() => {
  const TAG = 'subscribe-save';

  if (customElements.get(TAG)) return;

  class SubscribeSave extends HTMLElement {
    #data = {
      requiresSellingPlan: false,
      defaultPlanId: null,
      plans: [],
      allocations: {},
    };

    connectedCallback() {
      this.#data = this.#readData();
      this.#bind();
      this.#sync();

      this.#productForm()?.addEventListener('variant:change', this.#onVariantChange);
    }

    disconnectedCallback() {
      this.#productForm()?.removeEventListener('variant:change', this.#onVariantChange);

      for (const radio of this.#purchaseRadios()) {
        radio.removeEventListener('change', this.#onPurchaseChange);
      }

      this.#frequencySelect()?.removeEventListener('change', this.#onFrequencyChange);

      for (const card of this.#cards()) {
        card.removeEventListener('click', this.#onCardClick);
      }
    }

    #productForm() {
      return this.closest('product-form') || this.closest('[data-product-form]');
    }

    #readData() {
      const script = this.querySelector('[data-subscribe-save-json]');

      if (!script) return this.#data;

      try {
        return {
          ...this.#data,
          ...JSON.parse(script.textContent || '{}'),
        };
      } catch {
        return this.#data;
      }
    }

    #bind() {
      for (const radio of this.#purchaseRadios()) {
        radio.addEventListener('change', this.#onPurchaseChange);
      }

      this.#frequencySelect()?.addEventListener('change', this.#onFrequencyChange);

      for (const card of this.#cards()) {
        card.addEventListener('click', this.#onCardClick);
      }
    }

    #cards() {
      return this.querySelectorAll('[data-subscribe-save-card]');
    }

    #purchaseRadios() {
      return this.querySelectorAll('[data-subscribe-save-option]');
    }

    #frequencySelect() {
      return this.querySelector('[data-subscribe-save-frequency]');
    }

    #sellingPlanInput() {
      return this.querySelector('[data-selling-plan-input]');
    }

    #subscribeChecked() {
      const radio = this.querySelector('[data-subscribe-save-option="subscribe"]');

      return Boolean(radio?.checked) || this.#data.requiresSellingPlan;
    }

    #currentVariantId() {
      const form = this.closest('form');
      const input = form?.querySelector('[data-product-variant-id], [name="id"]');

      if (input instanceof HTMLInputElement && input.value) {
        return String(input.value);
      }

      const picker = this.#productForm()?.querySelector('product-variant-picker');
      const variant = picker?.getCurrentVariant?.();

      return variant?.id != null ? String(variant.id) : '';
    }

    #allocationFor(variantId) {
      const allocations = this.#data.allocations || {};
      const key = String(variantId);

      return allocations[key] || allocations[Number(key)] || null;
    }

    #planFromAllocation(allocation, planId) {
      if (!allocation?.plans) return null;

      const key = String(planId);

      return allocation.plans[key] || allocation.plans[Number(key)] || null;
    }

    #selectedPlanId() {
      const select = this.#frequencySelect();

      if (select instanceof HTMLSelectElement && select.value) {
        return String(select.value);
      }

      return this.#data.defaultPlanId != null ? String(this.#data.defaultPlanId) : '';
    }

    #onCardClick = (event) => {
      if (event.target.closest('select')) return;
      if (event.target.closest('input[type="radio"]')) return;

      const card = event.currentTarget;
      const radio = card.querySelector('[data-subscribe-save-option]');

      if (!(radio instanceof HTMLInputElement) || radio.checked) return;

      radio.checked = true;
      this.#sync();
    };

    #onPurchaseChange = () => {
      this.#sync();
    };

    #onFrequencyChange = () => {
      this.#sync();
    };

    #onVariantChange = () => {
      this.#sync();
    };

    #ensurePlanForVariant(variantId) {
      const allocation = this.#allocationFor(variantId);
      const select = this.#frequencySelect();

      if (!(select instanceof HTMLSelectElement) || !allocation?.plans) return;

      const currentId = String(select.value || '');

      if (this.#planFromAllocation(allocation, currentId)) return;

      const firstId = Object.keys(allocation.plans)[0];

      if (firstId) select.value = firstId;
    }

    #sync() {
      const variantId = this.#currentVariantId();

      this.#ensurePlanForVariant(variantId);

      const allocation = this.#allocationFor(variantId);
      const allocationMap = this.#data.allocations || {};
      const hasAllocationData = Object.keys(allocationMap).length > 0;
      const hasPlans = Boolean(allocation && Object.keys(allocation.plans || {}).length);

      // Only toggle visibility when allocation JSON actually loaded. A parse
      // failure used to hide the Liquid-rendered widget on first paint.
      if (hasAllocationData) {
        this.hidden = !hasPlans;
      }

      if (!hasPlans) {
        if (hasAllocationData) this.#setSellingPlan('');
        return;
      }

      const subscribe = this.#subscribeChecked();
      const planId = this.#selectedPlanId();
      const plan = this.#planFromAllocation(allocation, planId);
      const oneTimePrice = Number(allocation.price || 0);

      this.#setSellingPlan(subscribe && plan ? planId : '');
      this.#updateOneTimePrice(oneTimePrice);
      this.#updatePlanPrices(plan, oneTimePrice);
    }

    #setSellingPlan(planId) {
      const input = this.#sellingPlanInput();

      if (!(input instanceof HTMLInputElement)) return;

      const next = planId ? String(planId) : '';

      input.value = next;
      input.disabled = next === '';
    }

    #updateOneTimePrice(cents) {
      const node = this.querySelector('[data-subscribe-save-onetime-price]');

      if (node) node.textContent = this.#formatMoney(cents);
    }

    #updatePlanPrices(plan, oneTimePrice) {
      const planPrice = Number(plan?.price || 0);
      const compareAt = Number(plan?.compareAtPrice || oneTimePrice);
      const showSaving = compareAt > planPrice;
      const savingPercent = showSaving
        ? Math.round(((compareAt - planPrice) * 100) / compareAt)
        : 0;
      const visible = showSaving && savingPercent > 0;

      const planPriceNode = this.querySelector('[data-subscribe-save-plan-price]');
      const compareRoot = this.querySelector('[data-subscribe-save-compare]');
      const compareValue = this.querySelector('[data-subscribe-save-compare-value]');
      const badge = this.querySelector('[data-subscribe-save-badge]');
      const badgeLabel = this.querySelector('[data-subscribe-save-badge-label]');

      if (planPriceNode) planPriceNode.textContent = this.#formatMoney(planPrice);

      if (compareValue) compareValue.textContent = this.#formatMoney(compareAt);

      if (compareRoot instanceof HTMLElement) compareRoot.hidden = !visible;

      if (badge instanceof HTMLElement) badge.hidden = !visible;

      if (badgeLabel && visible) {
        const template = this.dataset.saveTemplate || 'SAVE PERCENT%';

        badgeLabel.textContent = template.replace('PERCENT', String(savingPercent));
      }
    }

    #formatMoney(cents) {
      const amount = Number(cents) || 0;
      const format = this.dataset.moneyFormat || this.#data.moneyFormat;

      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(amount, format);
      }

      const value = (amount / 100).toFixed(2);

      if (!format) return value;

      return format
        .replace(/\{\{\s*amount\s*\}\}/, value)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(amount / 100)))
        .replace(
          /\{\{\s*amount_with_comma_separator\s*\}\}/,
          value.replace('.', ',')
        )
        .replace(
          /\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/,
          String(Math.round(amount / 100))
        );
    }
  }

  customElements.define(TAG, SubscribeSave);
})();
